import { Checkbox } from '@progress/kendo-react-inputs';
import { Drawer, DrawerContent, DrawerItem } from '@progress/kendo-react-layout';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAliceRecommendedAttributes, getAliceRecommendedEntities } from '../../../../API/AliceGPT/AliceGPT';
import * as AliceGPTEvents from '../../../../Context/AliceGPT/AliceGPTContextEvents';
import useAliceGPT from '../../../../Hooks/AliceGPT/useAliceGPT';
import Scrollable from '../../../Shared/Edit/Scrollable';
import AliceRecommendedAttributesList from './AliceRecommendedAttributesList';
import Loader from '../../../Shared/Loader/loader';
import InnerLoader from '../../../Shared/Loader/InnerLoader';
import { getUserProfileEntityWithTranslation, USER_PROFILE_ENTITY } from './variables';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { getDatalakeImageUrl } from '../../../Helpers/DateLakeStorage';
import useFeatureFlags from '../../../../Hooks/FeatureFlag/useFeatureFlags';
import FailedToLoadError from '../../../Shared/Recommendation/Error/FailedToLoadError';
import { convertTypeToDesignerType } from "../../../Helpers/Recommendation/AliceGPT";
import { useTranslation } from 'react-i18next';
import AddNewObjectDialog from './AddNewObjectDialog';
import { getRandomIcon } from '../../../AIAgent/utils';

function AliceRecommendedEntities({ isFromExtension, descriptionRef }) {

    const currentInput = useRef(null);
    const currentInputs = {};
    const currentEditButton = useRef(null);

    const [state, dispatch] = useAliceGPT();
    const featureFlags = useFeatureFlags();

    const { i18n } = useTranslation();
    const { t } = useTranslation("create_project/alice_recommended_entities", {
        useSuspense: false
    })
    debugger;
    const { t: userProfileTranslator } = useTranslation("create_project/alice_recommended_entities", {
        useSuspense: false,
        lng: state.form.language || i18n.language || "en"
    })

    const [loading, setLoading] = useState(state.formUpdated);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addDialogValues, setAddDialogValues] = useState({ name: '', description: '', autofill: true });

    let numSelectedEntities = state.entities.filter(entity => entity.checked).length;

    useEffect(() => {
        if (state.formUpdated) {
            const { projectName, domain, description, model, language } = state.form;
            getAliceRecommendedEntities(
                domain || projectName, description, model, "", descriptionRef.current, language, isFromExtension
            )
                .then(response => {
                    const entities = response.map(entityToStateArrayElement)
                    entities.forEach((entity, index) => loadAttributesOfEntity(entity, true, index));
                    if (featureFlags.isUserProfileEntityFeatureEnabled) {
                        entities.unshift(
                            getUserProfileEntityWithTranslation(userProfileTranslator)
                        );
                    }
                    dispatch({ type: AliceGPTEvents.ENTITIES_LOADED, payload: entities });
                })
                .catch(error => {
                    console.log(error);
                    dispatch({
                        type: AliceGPTEvents.FACED_ERROR,
                        payload: {
                            error: error.message
                        }
                    })
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, []);

    function entityToStateArrayElement(entity, index) {
        return {
            name: entity[0],
            icon: entity[1],
            checked: (state.entities.length === 0 || state.formUpdated) && index < 5,
            attributesLoading: true,
            attributes: [],
            entityIndex: index
        }
    }

    async function loadAttributesOfEntity(entity, checked, index) {
        try {
            const attributes = await getAliceRecommendedAttributes(
                entity.name, state.form.model, "", state.form.domain || state.form.projectName, state.form.description, descriptionRef.current, state.form.language, isFromExtension
            );
            const attributesWithPicklist = attributes.map(
                (attribute, aIndex) => attributesToStateArrayElement(attribute, checked && aIndex < 10)
            );
            dispatch({
                type: AliceGPTEvents.ATTRIBUTES_LOADED,
                payload: {
                    attributes: attributesWithPicklist,
                    entityName: entity.name,
                    shouldCheckEntity: checked
                }
            });
        } catch (error) {
            dispatch({
                type: AliceGPTEvents.ENTITY_ATTRIBUTES_LOAD_FAILED,
                payload: {
                    entityName: entity.name
                }
            })
        }
    }

    function attributesToStateArrayElement(attribute, checked) {
        let picklist = attribute[2] || "";
        if ((attribute[1] === "picklist" || attribute[1] === "state") && (!picklist || picklist.length === 0)) {
            picklist = ["Value 1", "Value 2", "Value 3"];
        }
        return {
            name: attribute[0],
            type: convertTypeToDesignerType(attribute[1]),
            picklist: picklist,
            unique: attribute[3],
            checked: checked,
            selected: checked,
            disabled: attribute.disabled
        }
    }

    function toDrawerItem(entity) {
        const isMoreThanTwoStateAttributesSelected = entity.attributes.filter(attribute => (
            attribute.type === "State Field" && attribute.selected
        )).length > 1;
        const hasAttributeLimitExceededError = entity.attributes.filter(attribute => (
            attribute.selected
        )).length > state.subscription.maxAttributesPerApp;
        const hasStateOrDropdownWithNoValues = entity.attributes.some(attribute => (
            (attribute.type === "State Field" || attribute.type === "Picklist String") &&
            (!attribute.picklist || attribute.picklist.length === 0) &&
            attribute.selected
        ));
        const hasSameNameAsAnotherEntity = state.entities.filter(e => e.checked && e.name.trim() === entity.name.trim()).length > 1;
        const hasEmplyOrNumberEntityName = entity.name.trim().length === 0 || /^\d/g.test(entity.name.trim());
        const hasMoreThan30CharsInName = entity.name.trim().length > 30;
        const entityHasAttributesWithSameName = entity.attributes.some((attribute) => {
            if (attribute.selected) {
                return entity.attributes.filter((a) =>
                    a.selected &&
                    a.name.trim() === attribute.name.trim()
                ).length > 1
            }
            return false;
        })
        const entityHasEmptyOrStartWithNumberAttributesName = entity.attributes.some(attribute => (
            attribute.selected &&
            (attribute.name.trim().length === 0 || /^\d/g.test(attribute.name.trim()))
        ));
        const entityHasAttributeWithNameMoreThan30Chars = entity.attributes.some(attribute => (
            attribute.selected &&
            attribute.name.trim().length > 30
        ));
        const hasNoAttributes = entity.checked && !entity.attributesLoading && entity.attributes.length === 0;
        const hasOnlyHtmlAttributes = entity.checked && entity.attributes.filter(attribute => attribute.selected).length > 0 && 
            entity.attributes.filter(attribute => attribute.selected).every(attribute => attribute.type === "Html");

        const hasErrors = (
            isMoreThanTwoStateAttributesSelected ||
            hasStateOrDropdownWithNoValues ||
            hasAttributeLimitExceededError ||
            hasSameNameAsAnotherEntity ||
            entityHasEmptyOrStartWithNumberAttributesName ||
            hasEmplyOrNumberEntityName ||
            entityHasAttributesWithSameName ||
            hasMoreThan30CharsInName ||
            entityHasAttributeWithNameMoreThan30Chars ||
            hasNoAttributes ||
            hasOnlyHtmlAttributes
        );
        return {
            text: entity.name,
            disabled: entity.disabled,
            selected: entity.entityIndex === state.selectedEntity.entityIndex,
            checked: entity.checked,
            isUserEntity: entity.isUserEntity,
            attributesLoading: entity.attributesLoading,
            inEdit: entity.inEdit,
            hasErrors
        }
    }

    function handleEntitySelect(event) {
        if (event.nativeEvent.target.tagName !== "INPUT") {
            const entity = state.entities[event.itemIndex - 1];
            dispatch({ type: AliceGPTEvents.ENTITY_SELECTED, payload: entity });
        }
    }

    function handleEntityCheck(entityIndex) {
        if (state.entities[entityIndex].checked) {
            dispatch({ type: AliceGPTEvents.ENTITY_UNCHECKED, payload: { entityIndex } });
        } else {
            dispatch({ type: AliceGPTEvents.ENTITY_CHECKED, payload: { entityIndex } });
        }
    }

    function handleShowMore() {
        setFetchingMore(true);
        const presentEntityNames = state.entities.map(entity => entity.name).join(', ');
        getAliceRecommendedEntities(
            state.form.domain || state.form.projectName, state.form.description, state.form.model, presentEntityNames, state.form.workItemInformation, state.form.language?.code, isFromExtension
        )
            .then(response => {
                const entities = response.map(entityToStateArrayElement)
                entities.forEach((entity) => loadAttributesOfEntity(entity, false));
                dispatch({ type: AliceGPTEvents.ENTITIES_ADDED, payload: entities });
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setFetchingMore(false);
            });
    }

    function toggleEntityEdit(entityIndex) {
        return (event) => {
            currentEditButton.current = event.target;
            dispatch({ type: AliceGPTEvents.TOGGLE_ENTITY_EDIT, payload: { entityIndex: entityIndex - 1 } });
        }
    }

    function handleSaveEntityName(index) {
        return (event) => {
            const entityIndex = index - 1;
            const newEntityName = currentInputs[index].value;
            if (newEntityName !== state.entities[entityIndex].name) {
                dispatch({
                    type: AliceGPTEvents.ENTITY_NAME_CHANGED,
                    payload: {
                        entityIndex,
                        newEntityName
                    }
                });
            }
            dispatch({
                type: AliceGPTEvents.TOGGLE_ENTITY_EDIT,
                payload: {
                    entityIndex: entityIndex
                }
            });
        }
    }

    function handleAddNewObjectSubmit(values) {
        // Create a new entity object similar to entityToStateArrayElement
        const newEntity = {
            name: values.name.trim(),
            icon: getRandomIcon(),
            checked: true,
            attributesLoading: !!values.autofill,
            attributes: [],
            entityIndex: 0, // will be re-indexed if needed
            description: values.description || '',
        };
        let updatedEntities;
        // If first entity is user profile entity, insert as second
        if (state.entities.length > 0 && state.entities[0].isUserEntity) {
            updatedEntities = [
                state.entities[0],
                { ...newEntity, entityIndex: 1 },
                ...state.entities.slice(1).map((e, i) => ({ ...e, entityIndex: i + 2 }))
            ];
        } else {
            updatedEntities = [
                { ...newEntity, entityIndex: 0 },
                ...state.entities.map((e, i) => ({ ...e, entityIndex: i + 1 }))
            ];
        }
        dispatch({ type: AliceGPTEvents.ENTITIES_LOADED, payload: updatedEntities });
        // If autofill, load attributes for the new entity
        if (values.autofill) {
            loadAttributesOfEntity(newEntity, true, state.entities[0] && state.entities[0].isUserEntity ? 1 : 0);
        }
        setAddDialogOpen(false);
        setAddDialogValues({ name: '', description: '', autofill: true });
    }

    function hasEmptyEntityNames() {
        return state.entities.some(entity => entity.name.trim().length === 0);
    }

    function hasEntityNameStartingWithNumber() {
        return state.entities.some(entity => entity && /^\d/g.test(entity.name));
    }

    function hasDuplicateEntityNames() {
        return state.entities.some((entity, index) => entity && entity.checked && isNameReused(entity.name, index));
    }

    function hasEntityNamesWithMoreThan30Characters() {
        return state.entities.some(entity => entity.name.trim().length > 30);
    }

    function hasEntityWithNoAttributes() {
        return state.entities.some(entity => entity.checked && !entity.attributesLoading && entity.attributes.length === 0);
    }

    function hasEntityWithOnlyHtmlAttributes() {
        return state.entities.some(entity => {
            if (!entity.checked) return false;
            const selectedAttributes = entity.attributes.filter(attribute => attribute.selected);
            // If no attributes are selected, this check doesn't apply
            if (selectedAttributes.length === 0) {
                return false;
            }
            // Check if all selected attributes are HTML type
            return selectedAttributes.every(attribute => attribute.type === "Html");
        });
    }

    function isNameReused(name, index) {
        return state.entities.some((entity, aI) => entity.checked && entity.name.trim().toLowerCase() === name.trim().toLowerCase() && aI !== index);
    }

    // Checks if an entity name is already used (case-insensitive, trimmed)
    function isEntityNameUsed(name) {
        return state.entities.some(entity => entity.name.trim().toLowerCase() === name.trim().toLowerCase());
    }

    if (loading) {
        return (
            <div className='d-flex flex-column justify-content-center align-items-center h-100'>
                <img src={getDatalakeImageUrl('loaders/RecommendationLoader.gif')} alt="" />
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        );
    }

    if (state.error) {
        return (
            <FailedToLoadError />
        )
    }

    return (
        <div className='h-100'>
            <Drawer
                expanded
                width={300}
                position='start'
                mode='push'
                className='recommended-entities-drawer'
                items={
                    [
                        {
                            text: "Objects",
                            isHeading: true,
                            onClick: () => { }
                        },
                        ...state.entities.map(toDrawerItem),
                        {
                            text: 'Show More',
                            selected: false,
                            checked: false,
                            onClick: handleShowMore,
                            isShowMoreOption: true
                        }
                    ]
                }
                onSelect={handleEntitySelect}
                item={(props) => {
                    return (
                        <DrawerItem
                            {...props}
                            disabled={false}
                            className="d-flex align-items-center p-3 recommended-item-with-edit"
                        >
                            {
                                props.isHeading &&
                                <>
                                    <div className='d-flex justify-content-start flex-column'>
                                        <div className='d-flex align-items-center'>
                                            <Checkbox
                                                checked={state.entities.every(entity => entity.checked)}
                                                value={state.entities.every(entity => entity.checked)}
                                                onChange={() => dispatch({ type: AliceGPTEvents.TOGGLE_SELECT_ALL_ENTITIES })} />

                                            <Tooltip position='right' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                                                <span className='ms-2' style={{ fontWeight: "700" }}>
                                                    {t("drawer.select_all.label")}
                                                    <i
                                                        className='fa-solid fa-circle-info' style={{ opacity: "0.5" }}
                                                        title={t("drawer.select_all.tooltip")}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                        {
                                            numSelectedEntities === 1 &&
                                            <div className='text-danger'>
                                                {t("drawer.select_at_least_two_objects")}
                                            </div>
                                        }
                                        {
                                            numSelectedEntities > state.subscription.maxEntitiesPerApp ?
                                                <span className='text-danger'>
                                                    &nbsp;{t("drawer.max_entities_reached", { maxEntities: state.subscription.maxEntitiesPerApp })}
                                                </span>
                                                :
                                                null
                                        }
                                        {
                                            hasDuplicateEntityNames() &&
                                            <div className="text-danger font-size-1">
                                                {t("drawer.duplicate_entities_names")}
                                            </div>
                                        }
                                        {
                                            hasEmptyEntityNames() &&
                                            <div className="text-danger font-size-1">
                                                {t("drawer.blank_entities_name")}
                                            </div>
                                        }
                                        {
                                            hasEntityNameStartingWithNumber() &&
                                            <div className="text-danger font-size-1">
                                                {t("drawer.entities_name_starting_with_number")}
                                            </div>
                                        }
                                        {
                                            hasEntityNamesWithMoreThan30Characters() &&
                                            <div className="text-danger font-size-1">
                                                {t("drawer.entities_name_more_than_30_characters")}
                                            </div>
                                        }
                                        {
                                            hasEntityWithNoAttributes() &&
                                            <div className="text-danger font-size-1">
                                                {t("drawer.object_must_have_properties")}
                                            </div>
                                        }
                                        <div className='d-flex flex-column w-75'>
                                            {/* + New Object button below select all */}
                                            <button
                                                className="btn btn-primary mt-3 align-self-start w-100"
                                                onClick={() => setAddDialogOpen(true)}
                                                type="button"
                                            >
                                                {t('drawer.add_new_object.button')}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            }
                            {
                                props.isShowMoreOption &&
                                <>
                                    <button className='btn btn-link font-size-2' disabled={fetchingMore}>
                                        {t("drawer.show_more.label")}
                                    </button>
                                    {
                                        fetchingMore ?
                                            <InnerLoader borderColor="green" />
                                            :
                                            <>&nbsp;</>
                                    }
                                </>
                            }
                            {!props.isHeading && !props.isShowMoreOption && !props.inEdit &&
                                <div className='d-flex w-100 justify-content-between'>
                                    <span className='d-flex align-items-center' style={{ width: "85%" }}>
                                        <Checkbox
                                            disabled={props.disabled}
                                            checked={props.checked}
                                            value={props.checked}
                                            onChange={() => handleEntityCheck(props.index - 1)}
                                        />
                                        &nbsp;
                                        &nbsp;
                                        {
                                            props.isUserEntity ?
                                                <Tooltip
                                                    position='right'
                                                    anchorElement='target'
                                                    tooltipStyle={{ maxWidth: "250px" }}
                                                >
                                                    <span>
                                                        {props.text}
                                                        <i
                                                            className='fa-solid fa-circle-info user-profile-info' style={{ opacity: "0.5" }}
                                                            title={t("drawer.user_entity.tooltip")}
                                                        />
                                                    </span>
                                                </Tooltip>
                                                :
                                                <span className='text-truncate'>
                                                    {props.text}
                                                    {
                                                        props.attributesLoading &&
                                                        <>
                                                            &nbsp;
                                                            <InnerLoader borderColor="blue" />
                                                        </>
                                                    }
                                                    {
                                                        props.isUserEntity &&
                                                        <i
                                                            className='fa-solid fa-circle-info' style={{ opacity: "0.5" }}
                                                            title={t("drawer.user_entity.tooltip")}
                                                        />
                                                    }
                                                </span>
                                        }
                                    </span>
                                    <span className='d-flex align-items-center'>
                                        {
                                            props.hasErrors &&
                                            <span>
                                                <i className='fa-solid fa-circle-info text-danger' />
                                            </span>
                                        }
                                        {
                                            !props.isUserEntity &&
                                            <button
                                                className='btn p-0 border-0 recommendation-edit-name-button'
                                                onClick={toggleEntityEdit(props.index)}
                                            >
                                                <i className='fal fa-edit font-size-0' />
                                            </button>
                                        }
                                    </span>
                                </div>
                            }
                            {
                                props.inEdit &&
                                <div className='w-100 d-flex align-items-center justify-content-between'>
                                    <input
                                        ref={input => {
                                            currentInputs[props.index] = input;
                                        }}
                                        autoFocus
                                        className={'form-control w-75'}
                                        defaultValue={props.text}
                                    />
                                    <button className="btn p-0 border-0" onClick={handleSaveEntityName(props.index)}>
                                        <i className='fa-solid fa-check text-success' />
                                    </button>
                                </div>
                            }
                        </DrawerItem>
                    )
                }}
            >
                <DrawerContent className='h-100'>
                    <AliceRecommendedAttributesList descriptionRef={descriptionRef} />
                </DrawerContent>
            </Drawer>
            {/* Add New Object Dialog */}
            {addDialogOpen && (
                <AddNewObjectDialog
                    visible={addDialogOpen}
                    onClose={() => setAddDialogOpen(false)}
                    onSubmit={handleAddNewObjectSubmit}
                    values={addDialogValues}
                    onChange={setAddDialogValues}
                    isEntityNameUsed={isEntityNameUsed}
                    maxEntities={state.subscription.maxEntitiesPerApp}
                    entities={state.entities}
                />
            )}
        </div>
    )
}

export default AliceRecommendedEntities;